import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CW_TIERS } from "@/components/CardWarsPacks";
import { Swords, Flame, Shield, Package, Loader2, Save, Zap } from "lucide-react";

const Field = ({ label, value, onChange, step = "0.5", prefix, testId, w = "w-24" }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[8px] font-black uppercase tracking-[0.2em] text-zinc-500">{label}</span>
    <span className="flex items-center gap-1">
      {prefix && <span className="text-xs text-zinc-400 font-mono">{prefix}</span>}
      <input data-testid={testId} type="number" min="0" step={step} value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${w} bg-pb-elevated border border-pb-border px-2 py-1.5 text-sm font-mono outline-none focus:border-yellow-400`} />
    </span>
  </label>
);

const Toggle = ({ label, on, onClick, testId, activeCls = "bg-[#A3E635] text-black border-[#A3E635]" }) => (
  <button data-testid={testId} onClick={onClick}
    className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 border transition-colors ${on ? activeCls : "border-pb-border text-zinc-500 hover:border-zinc-400"}`}>
    {label}
  </button>
);

const TierSelect = ({ value, onChange, allowNone, testId }) => (
  <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value || null)}
    className="bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs font-mono outline-none focus:border-yellow-400">
    {allowNone && <option value="">— none —</option>}
    {CW_TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
  </select>
);

function PackEditor({ p, onChange }) {
  const set = (k, v) => onChange({ ...p, [k]: v });
  const setOdd = (t, v) => onChange({ ...p, odds: { ...p.odds, [t]: v } });
  const sum = CW_TIERS.reduce((a, t) => a + (Number(p.odds?.[t.key]) || 0), 0);
  return (
    <div className={`border p-4 space-y-3 ${p.limited ? "border-pb-red/50" : "border-pb-border"} ${p.active ? "bg-pb-elevated/30" : "bg-black/40 opacity-70"}`}
      data-testid={`cwc-pack-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid={`cwc-pack-${p.id}-name`} value={p.name} onChange={(e) => set("name", e.target.value)}
          className="flex-1 min-w-[140px] bg-pb-elevated border border-pb-border px-2 py-1.5 text-sm font-display font-black uppercase outline-none focus:border-yellow-400" />
        <Toggle label={p.active ? "Active" : "Off"} on={p.active} onClick={() => set("active", !p.active)} testId={`cwc-pack-${p.id}-active`} />
        <Toggle label="Limited" on={p.limited} onClick={() => set("limited", !p.limited)} testId={`cwc-pack-${p.id}-limited`}
          activeCls="bg-pb-red text-white border-pb-red" />
      </div>
      <div className="flex flex-wrap gap-3">
        <Field label="Dollar Price" prefix="$" value={p.usd} onChange={(v) => set("usd", v)} step="1" testId={`cwc-pack-${p.id}-usd`} w="w-20" />
        <Field label="Coin Price" prefix="🪙" value={p.blc} onChange={(v) => set("blc", v)} step="1" testId={`cwc-pack-${p.id}-blc`} w="w-20" />
        <Field label="Cards" value={p.cards} onChange={(v) => set("cards", v)} step="1" testId={`cwc-pack-${p.id}-cards`} w="w-16" />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[8px] font-black uppercase tracking-[0.2em] text-zinc-500">Odds per card</span>
          <span className={`text-[8px] font-mono font-bold px-1.5 py-0.5 border ${sum === 100 ? "border-[#A3E635]/60 text-[#A3E635]" : "border-pb-red/60 text-pb-red"}`}
            data-testid={`cwc-pack-${p.id}-odds-sum`}>Σ {sum}%</span>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {CW_TIERS.map((t) => (
            <label key={t.key} className="flex flex-col gap-0.5">
              <span className={`text-[7.5px] font-black uppercase tracking-widest ${t.cls}`}>{t.label}</span>
              <input data-testid={`cwc-pack-${p.id}-odds-${t.key}`} type="number" min="0" max="100" step="1"
                value={p.odds?.[t.key] ?? 0} onChange={(e) => setOdd(t.key, e.target.value)}
                className="w-full bg-pb-elevated border border-pb-border px-1.5 py-1 text-xs font-mono outline-none focus:border-yellow-400" />
            </label>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <span className="text-[8px] font-black uppercase tracking-[0.2em] text-zinc-500">Guarantee text (shown on the box)</span>
          <input data-testid={`cwc-pack-${p.id}-guarantee`} value={p.guarantee} onChange={(e) => set("guarantee", e.target.value)}
            className="bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs outline-none focus:border-yellow-400" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[8px] font-black uppercase tracking-[0.2em] text-zinc-500">Min tier</span>
          <TierSelect value={p.min_tier} onChange={(v) => set("min_tier", v)} testId={`cwc-pack-${p.id}-min-tier`} />
        </label>
        <Field label="Min #" value={p.min_count} onChange={(v) => set("min_count", v)} step="1" testId={`cwc-pack-${p.id}-min-count`} w="w-14" />
        <label className="flex flex-col gap-1">
          <span className="text-[8px] font-black uppercase tracking-[0.2em] text-zinc-500">OR alt tier</span>
          <TierSelect value={p.alt_tier} onChange={(v) => set("alt_tier", v)} allowNone testId={`cwc-pack-${p.id}-alt-tier`} />
        </label>
        <Field label="Alt #" value={p.alt_count} onChange={(v) => set("alt_count", v)} step="1" testId={`cwc-pack-${p.id}-alt-count`} w="w-14" />
      </div>
    </div>
  );
}

export default function CardWarsControl() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const load = () => api.get("/cardwars/admin/config").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-yellow-400 animate-spin" /></div>;

  const set = (section, k, v) => setCfg((c) => ({ ...c, [section]: { ...c[section], [k]: v } }));
  const setPack = (i, p) => setCfg((c) => ({ ...c, packs: c.packs.map((x, j) => (j === i ? p : x)) }));
  const oddsOk = cfg.packs.every((p) => CW_TIERS.reduce((a, t) => a + (Number(p.odds?.[t.key]) || 0), 0) === 100);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cardwars/admin/config", cfg);
      toast.success("Card Wars settings saved — live everywhere instantly");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const forceResolve = async () => {
    if (!window.confirm(`Resolve this week's Friday Night Battle NOW? ${cfg.event.entrants} entrants, ${cfg.event.pot} BLC pot — winner takes all immediately.`)) return;
    setResolving(true);
    try {
      const { data } = await api.post("/cardwars/admin/event/resolve");
      toast.success(`Resolved — ${data.winner_name} took ${data.pot_paid} BLC from ${data.entrants} players`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResolving(false); }
  };

  return (
    <div className="space-y-6" data-testid="cardwars-control">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="border border-pb-border bg-pb-elevated/30 p-4">
          <div className="flex items-center gap-2 mb-3 font-display font-black uppercase text-sm"><Swords className="w-4 h-4 text-yellow-400" /> Court Clash</div>
          <div className="flex flex-wrap gap-3">
            <Field label="Ranked entry (BLC)" value={cfg.clash.entry_blc} onChange={(v) => set("clash", "entry_blc", v)} testId="cwc-clash-entry" />
            <Field label="House cut %" value={cfg.clash.house_cut_pct} onChange={(v) => set("clash", "house_cut_pct", v)} step="1" testId="cwc-clash-cut" />
            <Field label="Daily bonus (BLC)" value={cfg.clash.daily_bonus_blc} onChange={(v) => set("clash", "daily_bonus_blc", v)} step="0.1" testId="cwc-clash-bonus" />
            <Field label="Win-rate cap %" value={cfg.clash.winrate_cap_pct} onChange={(v) => set("clash", "winrate_cap_pct", v)} step="1" testId="cwc-clash-wincap" w="w-16" />
          </div>
          <p className="mt-2 text-[9px] uppercase tracking-widest text-zinc-500" data-testid="cwc-live-winrate">
            House Always Wins: bots toughen up on players running above the cap ·
            live ranked win rate: <b className={cfg.clash.live_player_winrate != null && cfg.clash.live_player_winrate > cfg.clash.winrate_cap_pct ? "text-pb-red" : "text-[#A3E635]"}>
              {cfg.clash.live_player_winrate != null ? `${cfg.clash.live_player_winrate}%` : "no data yet"}</b>
          </p>
        </div>
        <div className="border border-pb-red/40 bg-red-950/10 p-4">
          <div className="flex items-center gap-2 mb-3 font-display font-black uppercase text-sm"><Flame className="w-4 h-4 text-pb-red" /> Friday Night Battle</div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Entry fee (BLC)" value={cfg.event.entry_blc} onChange={(v) => set("event", "entry_blc", v)} testId="cwc-event-entry" />
            <div className="text-[10px] font-mono text-zinc-400 leading-relaxed">
              Week {cfg.event.week_key}<br />
              <b className="text-yellow-400">{cfg.event.pot} BLC pot</b> · {cfg.event.entrants} in
            </div>
          </div>
          <button data-testid="cwc-event-resolve" onClick={forceResolve} disabled={resolving || cfg.event.entrants === 0}
            className="mt-3 w-full flex items-center justify-center gap-1.5 border border-pb-red/60 text-pb-red text-[9px] font-black uppercase tracking-widest py-2 hover:bg-pb-red hover:text-white transition-colors disabled:opacity-40">
            {resolving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Force-Resolve Now
          </button>
        </div>
        <div className="border border-teal-400/30 bg-teal-950/10 p-4">
          <div className="flex items-center gap-2 mb-3 font-display font-black uppercase text-sm"><Shield className="w-4 h-4 text-teal-300" /> Protection Economy</div>
          <div className="flex flex-wrap gap-3">
            <Field label="Sleeve (BLC)" value={cfg.protection.sleeve_fee_blc} onChange={(v) => set("protection", "sleeve_fee_blc", v)} testId="cwc-prot-sleeve" w="w-16" />
            <Field label="Top Loader (BLC)" value={cfg.protection.toploader_fee_blc} onChange={(v) => set("protection", "toploader_fee_blc", v)} testId="cwc-prot-toploader" w="w-16" />
            <Field label="Slab & Grade (BLC)" value={cfg.protection.slab_fee_blc} onChange={(v) => set("protection", "slab_fee_blc", v)} testId="cwc-prot-slab" w="w-16" />
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-3 font-display font-black uppercase text-sm">
          <Package className="w-4 h-4 text-yellow-400" /> Pack Manager
          <span className="text-[8px] font-normal normal-case tracking-widest text-zinc-500">— dollar + coin pricing, per-card odds, guarantees. Odds should total 100%.</span>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          {cfg.packs.map((p, i) => <PackEditor key={p.id} p={p} onChange={(np) => setPack(i, np)} />)}
        </div>
      </div>
      <button data-testid="cwc-save-btn" onClick={save} disabled={saving || !oddsOk}
        title={oddsOk ? "" : "Every pack's odds must total exactly 100%"}
        className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-black uppercase tracking-widest py-3.5 hover:bg-[#BEF264] active:scale-[0.99] transition-all disabled:opacity-40">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {oddsOk ? "Save All Card Wars Settings" : "Fix pack odds — each must total 100%"}
      </button>
    </div>
  );
}
